import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import type { PartnerActivityEntry } from "../../model";
import {
  ACTIVITY_COLUMNS,
  isActivitySortColumn,
  type ActivitySort,
  type ActivitySortColumn,
} from "../activityColumns";
import { ActivityCellContent } from "./ActivityCellContent";

interface PartnerActivityTableProps {
  entries: PartnerActivityEntry[];
  sort: ActivitySort;
  onSort: (column: ActivitySortColumn) => void;
}

/**
 * One partner's Recent Activity — Time / Type / Query / Results / Region /
 * Status, newest-first by default. A typed port of the POC's activity table
 * (`renderActivity`/`applyActivityView`), minus the per-row error-detail
 * download link (see `activityView.ts`'s TODO(PM-FR-007)).
 */
export function PartnerActivityTable({ entries, sort, onSort }: PartnerActivityTableProps) {
  return (
    <TableContainer sx={{ overflowX: "auto" }}>
      <Table size="small" aria-label="Recent activity">
        <TableHead>
          <TableRow>
            {ACTIVITY_COLUMNS.map((column) => (
              <TableCell
                key={column.id}
                align={column.numeric ? "right" : "left"}
                sortDirection={sort.column === column.id ? sort.direction : false}
              >
                {isActivitySortColumn(column.id) ? (
                  <TableSortLabel
                    active={sort.column === column.id}
                    direction={sort.column === column.id ? sort.direction : "asc"}
                    onClick={() => onSort(column.id as ActivitySortColumn)}
                  >
                    {column.label}
                  </TableSortLabel>
                ) : (
                  column.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.length === 0 ? (
            <TableRow>
              <TableCell colSpan={ACTIVITY_COLUMNS.length}>
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No activity matches the current filters.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            entries.map((entry, index) => (
              // Activity rows have no stable id in the model; time+index is
              // stable within one render of this (already sorted) array.
              <TableRow key={`${entry.time}-${index}`}>
                {ACTIVITY_COLUMNS.map((column) => (
                  <TableCell key={column.id} align={column.numeric ? "right" : "left"}>
                    <ActivityCellContent cell={column.project(entry)} />
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
